import React, { useState } from 'react';
import { MapPin, Plus, Edit2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

export const SavedAddresses = ({ userId }) => {
  // Saved addresses stored in localStorage for this implementation
  const [addresses, setAddresses] = useState(() => {
    const saved = localStorage.getItem(`addresses_${userId}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    address: '',
    city: '',
    postal_code: '',
    phone: ''
  });

  const saveAddresses = (newAddresses) => {
    localStorage.setItem(`addresses_${userId}`, JSON.stringify(newAddresses));
    setAddresses(newAddresses);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let updated;
    if (editingIndex !== null) {
      updated = [...addresses];
      updated[editingIndex] = formData;
      toast.success('Address updated');
    } else {
      updated = [...addresses, formData];
      toast.success('Address added');
    }
    
    saveAddresses(updated);
    resetForm();
  };

  const handleEdit = (index) => {
    setFormData(addresses[index]);
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDelete = (index) => {
    if (window.confirm('Delete this address?')) {
      const updated = addresses.filter((_, i) => i !== index);
      saveAddresses(updated);
      toast.success('Address deleted');
    }
  };

  const resetForm = () => {
    setFormData({ label: '', address: '', city: '', postal_code: '', phone: '' });
    setEditingIndex(null);
    setShowForm(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Saved Addresses
          </CardTitle>
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Address
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-muted/30 rounded-lg space-y-4">
            <div>
              <Label htmlFor="label">Label (e.g., Home, Work)</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit">{editingIndex !== null ? 'Update' : 'Save'} Address</Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        )}

        {addresses.length > 0 ? (
          <div className="space-y-3">
            {addresses.map((addr, index) => (
              <div key={index} className="border rounded-lg p-4 hover:border-primary transition-smooth">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{addr.label}</Badge>
                      {index === 0 && <Badge variant="default">Default</Badge>}
                    </div>
                    <p className="text-sm font-medium">{addr.address}</p>
                    <p className="text-sm text-muted-foreground">{addr.city}, {addr.postal_code}</p>
                    <p className="text-sm text-muted-foreground">{addr.phone}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(index)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !showForm && (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No saved addresses yet</p>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};